import { ref, onMounted } from "vue";
import api from "@/services/api";

// Module-level cache — survives component remounts, shared across all instances.
// Warm cache means useContent() returns real data synchronously with zero flash.
const cache = new Map<string, Record<string, any>>();
const inflight = new Map<string, Promise<Record<string, any> | null>>();

// Called by the router before the home page renders.
// Fetches every section in one request and warms the cache.
export async function preloadAllContent(): Promise<void> {
  try {
    const { data } = await api.get("/api/v1/content");
    for (const [section, content] of Object.entries(
      data.data as Record<string, Record<string, any>>
    )) {
      cache.set(section, content);
    }
  } catch {
    // Non-fatal — components will fall back to individual fetches.
  }
}

function fetchSection(section: string): Promise<Record<string, any> | null> {
  if (!inflight.has(section)) {
    const p = api
      .get(`/api/v1/content/${section}`)
      .then(({ data }) => {
        cache.set(section, data.content);
        return data.content as Record<string, any>;
      })
      .catch(() => null)
      .finally(() => inflight.delete(section));
    inflight.set(section, p);
  }
  return inflight.get(section)!;
}

export function useContent<T>(section: string, defaults: T) {
  const cached = cache.get(section);

  // If cache is warm, initialize synchronously — no loading state, no flash.
  const content = ref<T>(
    cached ? ({ ...defaults, ...cached } as T) : ({ ...defaults } as T)
  );
  const loading = ref(!cached);

  if (!cached) {
    onMounted(async () => {
      try {
        const data = await fetchSection(section);
        if (data) content.value = { ...defaults, ...data } as T;
      } finally {
        loading.value = false;
      }
    });
  }

  return { content, loading };
}
